import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetEffortByIdQuery } from 'store/api/efforts';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetMatterByIdQuery } from 'store/api/matters';
import { PartyType } from 'types/contacts';
import { Effort, EffortForm, RANKINGS_REFEREE_TYPE } from 'types/efforts';
import { EmployeeListItemMinDto } from 'types/employees';
import { FeatureFlag } from 'types/features';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { EFFORT_FORM_CLASSIFIERS, effortDtoToForm, newNominationLine } from './effortFormMapping';
import EffortPageForm from './EffortPageForm';

const EffortPageAdd = (): JSX.Element => {
    const { user } = useAuth();
    const userId = user?.id;
    const [initialData, setInitialData] = useState<EffortForm>();

    const { params, location } = useCreateRoute();
    const { contactId, matterId } = params;
    // `?activityType=RANKINGS_REFEREE` opens the form on that type, with its first nomination line.
    const presetActivityType = new URLSearchParams(location.search).get('activityType');
    const hasRankingsReferees = useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE);

    const { data: contact } = useGetContactByIdQuery(contactId ?? skipToken);
    const { data: matter } = useGetMatterByIdQuery(matterId ?? skipToken);
    useCreateDialogChip({ parent: contact?.name ?? matter?.fullName });

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery({ pageSize: 1, page: 0, filters: `id==${userId}` }, { skip: !userId });

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EFFORT_FORM_CLASSIFIERS });

    const getPartyCompanies = useCallback(() => {
        if (contact && contact.partyType === PartyType.COMPANY) {
            return [contact];
        }

        if (matter) {
            return [matter.clientParty];
        }

        return [];
    }, [contact, matter]);

    // Set initial form data
    const getInitialData = useCallback((): EffortForm | undefined => {
        if (classifiers) {
            const preSelectedEmployee = currentEmployeeData;
            const partyPersons = contact && contact.partyType === PartyType.PERSON ? [contact] : [];
            const effortDate = formatDateStringInput(new Date());
            const presetRankingsReferee = hasRankingsReferees && presetActivityType === RANKINGS_REFEREE_TYPE;
            return {
                id: null,
                description: '',
                effortDate,
                effortByEmployee: preSelectedEmployee
                    ? { id: preSelectedEmployee?.id, name: preSelectedEmployee?.name }
                    : null,
                usedTimeInSeconds: null,
                usedTimeInHours: '',
                effortActivityType: presetRankingsReferee
                    ? ((
                          getValidOptionsFromClassifier(classifiers['EFFORT_ACTIVITY_TYPE']) as {
                              label: string;
                              value: string;
                          }[]
                      ).find(({ value }) => value === RANKINGS_REFEREE_TYPE) ?? null)
                    : null,
                employees: [],
                matters: matter ? [matter] : [],
                partyPersons,
                partyCompanies: getPartyCompanies(),
                expenses: [],
                ...(presetRankingsReferee ? { nominations: [newNominationLine(effortDate, partyPersons)] } : {}),
            };
        }

        return undefined;
    }, [classifiers, currentEmployeeData, matter, contact, getPartyCompanies, hasRankingsReferees, presetActivityType]);

    useEffect(() => {
        if (isLoadingCurrentEmployeeQueryData) {
            return;
        }
        setInitialData(getInitialData());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classifiers, userId, isLoadingCurrentEmployeeQueryData, contact, matter]);

    if (isLoadingClassifiers || isLoadingCurrentEmployeeQueryData || initialData === undefined) {
        return <Loader />;
    }

    return (
        <EffortPageForm
            type="add"
            classifiers={classifiers}
            initialData={initialData}
            fallbackUrl={
                contactId
                    ? `/contact/${contactId}?tab=bd&subtab=0`
                    : matterId
                      ? `/matter/${matterId}?tab=bd&subtab=0`
                      : undefined
            }
            expenses={[]}
        />
    );
};

/** The add form loaded with what a draft holds, its expense lines included, read as the effort's edit page reads it. */
const EffortDraftAdd = ({ draftId }: { draftId: number }) => {
    const [loaded, setLoaded] = useState<{ form: EffortForm; expenses: Effort['expenses'] }>();
    const { data: effort } = useGetEffortByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EFFORT_FORM_CLASSIFIERS });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!loaded && effort && !isLoadingClassifiers) {
        setLoaded({ form: effortDtoToForm(effort, classifiers), expenses: effort.expenses });
    }

    return loaded ? (
        <EffortPageForm
            type="add"
            classifiers={classifiers}
            initialData={loaded.form}
            expenses={loaded.expenses ?? []}
        />
    ) : (
        <Loader />
    );
};

/** The effort create; its URL with `?draft=<id>` edits that draft instead of a blank effort. */
const EffortPageAddOrDraft = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <EffortPageAdd /> : <EffortDraftAdd key={draftId} draftId={draftId} />;
};

export default EffortPageAddOrDraft;
